import { useState, useEffect } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { toast } from "react-toastify";
import { download } from "../../Utils/Utils";
import { EMimeTypes } from "../../Utils/Enums";
import { fetchDocumentTypes } from "../../Utils/DocumentTypesUtils";

interface GenerateDocumentDropdownProps {
  orderId?: string;
  clientData?: any;
  orderTypeId?: string;
  onDocumentGenerated?: () => void;
}

export default function GenerateDocumentDropdown({
  orderId,
  clientData,
  orderTypeId,
  onDocumentGenerated,
}: GenerateDocumentDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [documentTypeOptions, setDocumentTypeOptions] = useState<any[]>([]);
  const open = Boolean(anchorEl);

  useEffect(() => {
    fetchDocumentTypes().then((types) => {
      const filteredTypes = types.filter((docType: any) => {
        const typeOrderTypeIds: (string | { _id: string })[] = docType?.orderTypeIds || [];

        // Types without orderTypeIds are always shown
        if (typeOrderTypeIds.length === 0) {
          return true;
        }

        // If the current order has no orderTypeId, hide types that are restricted to a specific order type
        if (!orderTypeId) {
          return false;
        }

        return typeOrderTypeIds.some((ot: string | { _id: string }) =>
          (typeof ot === "string" ? ot : ot._id) === orderTypeId
        );
      });

      setDocumentTypeOptions(filteredTypes);
    });
  }, [orderTypeId]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGenerateDocument = async (document: any) => {
    if (!orderId || !clientData) {
      toast.error(Vocabulary.missingDataError);
      handleClose();
      return;
    }

    handleClose();
    try {
      const response = await postData(
        `${endpoints.document}/generate`,
        {
          documentTypeId: document?._id,
          orderId,
          clientId: clientData._id,
        },
        { responseType: "blob" }
      );

      if (response?.data) {
        const fileName = `${document.name}_${clientData.firstName}_${clientData.lastName}`;
        const blob = new Blob([response.data]);
        download(blob, fileName, EMimeTypes.DOCX);

        // Trigger refresh if callback provided
        if (onDocumentGenerated) {
          onDocumentGenerated();
        }
      }
    } catch (error) {
      console.error("Error generating document:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const isDisabled = !orderId || !clientData || isGenerating;

  return (
    <>
      <Button
        variant="contained"
        onClick={handleClick}
        disabled={isDisabled}
        endIcon={
          isGenerating ? <CircularProgress size={16} /> : <ArrowDownIcon />
        }
        sx={{ ml: 1 }}
      >
        <DescriptionIcon sx={{ mr: 1 }} />
        {Vocabulary.generateDocument}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "generate-document-button",
        }}
      >
        {documentTypeOptions.map((option) => (
          <MenuItem
            key={option.name}
            onClick={() => handleGenerateDocument(option)}
            disabled={isGenerating}
          >
            <ListItemIcon>
              <DescriptionIcon />
            </ListItemIcon>
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
